import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, CheckCircle, XCircle, AlertTriangle, Eye, Trash2, RefreshCw, Settings, Clock, CreditCard, Calendar, Ban } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Modal } from '@shared/components/ui/Modal';
import { PaywallModal } from '../components/PaywallModal';
import { useAccessControl } from '../hooks/useAccessControl';
import {
  canStartNewReport,
  getUserActiveSession,
  cancelSession,
  checkAndResumeActiveSession,
  type ActiveSession
} from '../services/sessionManagementService';

interface Report {
  id: string;
  title: string;
  target_role: string | null;
  industry: string | null;
  status: 'queued' | 'processing' | 'completed' | 'error';
  created_at: string;
  updated_at: string;
  extraction_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
}

interface LicenseStatus {
  valid: boolean;
  activated: boolean;
  license_start_date?: string;
  license_expiry_date?: string;
  days_remaining?: number;
  credits_remaining: number;
  total_credits: number;
  message?: string;
  error?: string;
}

const Reports: React.FC = () => {
  const { user } = useAuth();
  const accessControl = useAccessControl();
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pollingSessionId, setPollingSessionId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentStage, setCurrentStage] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [totalSteps, setTotalSteps] = useState<number>(9);
  const [currentPromptNumber, setCurrentPromptNumber] = useState<number>(0);
  const [totalPrompts, setTotalPrompts] = useState<number>(0);
  const [currentPromptName, setCurrentPromptName] = useState<string>('');
  const [estimatedCompletion, setEstimatedCompletion] = useState<string | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [canStartReport, setCanStartReport] = useState(true);
  const [activeSessionInfo, setActiveSessionInfo] = useState<ActiveSession | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  // DEBUG: Log state changes to diagnose status UI issues
  useEffect(() => {
    console.log('🔍 STATUS UI DEBUG:', {
      isGenerating,
      pollingSessionId,
      hasPollingId: !!pollingSessionId,
      shouldShowStatusUI: isGenerating && !!pollingSessionId
    });

    // REMOVED: The 30-second stuck state detection was causing false failures
    // The Status Manager now activates immediately when pollingSessionId is set
    // Any real failures will be caught by the Realtime subscription or backend
  }, [isGenerating, pollingSessionId]);

  useEffect(() => {
    if (user) {
      fetchReports(true);
      checkForActiveSessionImproved();
    }
  }, [user?.id]);

  const checkForActiveSessionImproved = async () => {
    if (!user) return;

    setIsCheckingSession(true);

    try {
      console.log('🔍 Checking for active sessions using improved service...');
      console.log('🔍 Current sessionStorage:', sessionStorage.getItem('activeReportSessionId'));

      // Use the enhanced service function to check and resume
      // This now automatically cleans up stuck sessions before checking
      const { shouldResume, session } = await checkAndResumeActiveSession(user.id);

      if (shouldResume && session) {
        console.log('📋 Resuming active session:', session.session_id);
        console.log(`   Progress: ${session.progress_percent}%, Step: ${session.current_step}/${session.total_steps}`);
        console.log(`   Stage: ${session.current_stage}, Status: ${session.status}`);

        // Additional safety check: Don't resume if session is at 0% and has no stage
        if (session.progress_percent === 0 && session.current_step === 0 && !session.current_stage) {
          console.warn('⚠️ Session has no progress data, not resuming');
          sessionStorage.removeItem('activeReportSessionId');
          setCanStartReport(true);
          setActiveSessionInfo(null);
          return;
        }

        // Set all UI state to show the active session IMMEDIATELY
        // This ensures Status Manager appears right away, not after a delay
        setPollingSessionId(session.session_id); // Set this FIRST
        setIsGenerating(true);
        setCurrentStage(session.current_stage || 'Processing');
        setCurrentStep(session.current_step || 0);
        setTotalSteps(session.total_steps || 9);
        setProgressPercent(session.progress_percent || 5); // Minimum 5% to show activity
        setActiveSessionInfo(session);
        setCanStartReport(false);

        // Store in sessionStorage for persistence
        sessionStorage.setItem('activeReportSessionId', session.session_id);

        console.log('✅ UI state set for resumed session, Status Manager should now be visible');
        toast.info(`Resuming report generation... (${session.progress_percent}% complete)`);
      } else {
        // No active session or it was stuck and cleaned up
        console.log('✅ No active sessions found or sessions were cleaned up');
        sessionStorage.removeItem('activeReportSessionId');
        setCanStartReport(true);
        setActiveSessionInfo(null);
        setIsGenerating(false);
        setPollingSessionId(null);
        console.log('✅ UI state cleared, user can start new report');
      }
    } catch (error: any) {
      console.error('❌ Error checking for active session:', error);

      // Log detailed error information for debugging
      if (error.message) console.error('   Error message:', error.message);
      if (error.code) console.error('   Error code:', error.code);
      if (error.details) console.error('   Error details:', error.details);

      // On error, clear any stuck state and allow user to proceed
      console.warn('⚠️ Could not verify active sessions. Clearing state and allowing user to proceed.');
      sessionStorage.removeItem('activeReportSessionId');
      setCanStartReport(true);
      setActiveSessionInfo(null);
      setIsGenerating(false);
      setPollingSessionId(null);

      // Show user-friendly message only for auth errors
      if (error.message?.includes('JWT') || error.message?.includes('auth')) {
        toast.warning('Session check failed. If you have issues, please refresh the page.', { duration: 4000 });
      }
    } finally {
      setIsCheckingSession(false);
    }
  };

  const formatStageName = (stage: string): string => {
    const stageMap: { [key: string]: string } = {
      'INITIALIZATION': 'Initializing...',
      'DATA_LOADING': 'Loading your profile data...',
      'PROMPT_LOADING': 'Loading AI configurations...',
      'CONTEXT_PREPARATION': 'Preparing analysis context...',
      'AI_ANALYSIS_START': 'Beginning AI analysis...',
      'AI_ANALYSIS_CONTINUE': 'Continuing AI analysis...',
      'AI_ANALYSIS_FINALIZE': 'Finalizing AI analysis...',
      'PROMPT_EXECUTION': 'Running AI analysis...',
      'REPORT_ASSEMBLY': 'Assembling your report...',
      'FINALIZATION': 'Finalizing report...',
      'ERROR': 'Error occurred'
    };

    // Handle batch processing stages
    if (stage.startsWith('BATCH_')) {
      const batchNum = stage.replace('BATCH_', '');
      return `Processing batch ${batchNum} of prompts...`;
    }

    if (stage.startsWith('EXECUTING_')) {
      const promptName = stage.replace('EXECUTING_', '').replace(/_/g, ' ');
      return `Analyzing: ${promptName.charAt(0) + promptName.slice(1).toLowerCase()}`;
    }

    return stageMap[stage] || stage.replace(/_/g, ' ').toLowerCase();
  };

  const getProcessingStages = (): string[] => {
    return [
      'Initialize Session',           // Step 0
      'Load Profile Data',            // Step 1
      'Configure AI Models',          // Step 2
      'Prepare Analysis',             // Step 3
      'AI Analysis (Start)',          // Step 4
      'AI Analysis (Middle)',         // Step 5
      'AI Analysis (Final)',          // Step 6
      'Assemble Report',              // Step 7
      'Finalize & Save'               // Step 8
    ];
  };

  const getDetailedStageDescription = (): string => {
    if (currentStage.includes('BATCH_')) {
      const batchMatch = currentStage.match(/BATCH_(\d+)/);
      if (batchMatch) {
        return `Processing batch ${batchMatch[1]} of prompts...`;
      }
    }

    if (totalPrompts > 0 && currentPromptNumber > 0) {
      return `AI Analysis: ${currentPromptName} (${currentPromptNumber}/${totalPrompts})`;
    }

    return formatStageName(currentStage);
  };

  // Session keep-alive effect - refresh session periodically when on reports page
  useEffect(() => {
    if (!user) return;

    let lastSessionState = true; // Track previous session state
    let sessionWarningCount = 0; // Limit warnings to prevent spam
    const MAX_SESSION_WARNINGS = 2; // Maximum warnings before stopping

    const keepAliveInterval = setInterval(async () => {
      try {
        console.log('🔄 Reports: Refreshing session to keep alive');
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('❌ Reports: Session refresh error:', error);
          return;
        }

        const hasSession = !!session;

        // Only show toast if session state changed from valid to invalid
        // and we haven't exceeded the warning limit
        if (lastSessionState && !hasSession && sessionWarningCount < MAX_SESSION_WARNINGS) {
          console.warn('⚠️ Reports: Session state changed from valid to invalid');
          toast.warning('Your session may have expired. Please refresh if you experience issues.');
          sessionWarningCount++;
        } else if (hasSession) {
          console.log('✅ Reports: Session is still active');
          // Reset warning count when session is valid
          sessionWarningCount = 0;
        }

        lastSessionState = hasSession;
      } catch (error) {
        console.error('❌ Reports: Keep-alive check exception:', error);
      }
    }, 60000);

    return () => clearInterval(keepAliveInterval);
  }, [user]);

  // CRITICAL FIX: Hybrid Realtime + Polling subscription for reliable progress tracking
  // Primary: Realtime for instant updates
  // Backup: Polling every 8 seconds if Realtime goes silent for 15+ seconds
  useEffect(() => {
    if (!pollingSessionId) return;

    console.log(`🔔 Setting up hybrid Realtime + Polling subscription for session: ${pollingSessionId}`);

    let lastProgressUpdate = Date.now();
    let lastKnownStep = -1;
    let lastKnownPrompt = -1;
    let stuckWarningShown = false;
    const STUCK_TIMEOUT = 30000;
    const REALTIME_SILENT_TIMEOUT = 15000; // Switch to polling if no Realtime updates for 15s
    let fallbackPollInterval: NodeJS.Timeout | null = null;
    let hybridPollInterval: NodeJS.Timeout | null = null;
    let stuckCheckInterval: NodeJS.Timeout | null = null;
    let realtimeActive = true;
    let lastRealtimeUpdate = Date.now();

    // Handler function for session updates (from Realtime or fallback polling)
    const handleSessionUpdate = async (session: any) => {
      try {
        if (!session) return;

        console.log(`📊 Session ${pollingSessionId} status: ${session.status} (Step ${session.current_step}/${session.total_steps})`);
        console.log(`📊 Session details:`, {
          status: session.status,
          current_step: session.current_step,
          total_steps: session.total_steps,
          current_stage: session.current_stage,
          updated_at: session.updated_at
        });

        const stage = session.current_stage || 'Processing';
        const step = session.current_step || 0;
        const total = session.total_steps || 9;
        const promptNum = session.current_prompt_number || 0;
        const promptTotal = session.total_prompts || 0;
        const promptName = session.current_prompt_name || '';

        // CRITICAL FIX: Improved progress calculation with multiple fallbacks
        // Priority: prompt_number > progress_details > current_step > initialization
        let percent;
        let progressSource = '';

        if (promptTotal > 0 && promptNum > 0) {
          // HIGHEST PRIORITY: Use prompt-based progress for most accurate tracking
          percent = Math.round((promptNum / promptTotal) * 100);
          progressSource = `prompt ${promptNum}/${promptTotal}`;
        } else if (session.progress_details?.percent_complete !== undefined && session.progress_details.percent_complete > 0) {
          // SECONDARY: Use progress_details if it has a meaningful value
          percent = Math.min(session.progress_details.percent_complete, 99); // Cap at 99% until confirmed complete
          progressSource = 'progress_details';
        } else if (step > 0) {
          // TERTIARY: Step-based calculation if we have a step value
          percent = Math.min(Math.round((step / total) * 100), 99);
          progressSource = `step ${step}/${total}`;
        } else {
          // FALLBACK: Minimum 5% to show activity at initialization
          percent = 5;
          progressSource = 'initialization default';
        }

        console.log(`📊 Calculated progress: ${percent}% (source: ${progressSource})`);

        // CRITICAL FIX: Enforce STRICT monotonic progress
        // Never allow progress to decrease - this prevents UI jumping backwards
        const currentDisplayedProgress = progressPercent;
        if (percent < currentDisplayedProgress && currentDisplayedProgress > 0) {
          console.warn(`⚠️ Monotonic constraint: ${currentDisplayedProgress}% → ${percent}% rejected, keeping ${currentDisplayedProgress}%`);
          percent = currentDisplayedProgress; // Keep the higher value
        } else if (percent > currentDisplayedProgress) {
          console.log(`✅ Progress advanced: ${currentDisplayedProgress}% → ${percent}%`);
        }

        console.log(`📊 Updating UI: Stage="${stage}", Step=${step}/${total}, Progress=${percent}% (monotonic enforced)`);
        if (promptNum > 0) {
          console.log(`   🤖 Prompt: ${promptNum}/${promptTotal} - ${promptName}`);
        }

        // Check if progress has actually changed (prioritize prompt number for accuracy)
        const hasProgressChanged = promptNum > lastKnownPrompt || (promptNum === 0 && step > lastKnownStep);

        if (hasProgressChanged) {
          lastProgressUpdate = Date.now();
          lastRealtimeUpdate = Date.now(); // Track Realtime activity
          lastKnownStep = step;
          lastKnownPrompt = promptNum;
          stuckWarningShown = false;
          realtimeActive = true; // Mark Realtime as working
          console.log(`✅ Progress advanced to step ${step}, prompt ${promptNum}`);
        } else {
          const timeSinceLastUpdate = Date.now() - lastProgressUpdate;
          console.log(`⏱️ Time since last progress: ${Math.round(timeSinceLastUpdate / 1000)}s`);

          if (timeSinceLastUpdate > STUCK_TIMEOUT && !stuckWarningShown) {
            console.warn('⚠️ Session appears stuck - no progress for 30+ seconds');
            console.log(`📊 Session state: step=${step}, stage=${stage}, lastHeartbeat=${session.last_heartbeat}`);
            console.log(`📊 Checking if batch processing is the issue...`);

            // Check if this is a batch processing session that might have failed
            if (session.execution_mode === 'batch') {
              console.warn('⚠️ Batch processing session appears stuck');
              toast.warning('Processing is taking longer than expected. The system has automatic recovery mechanisms that may resume progress. If no activity after 60 seconds, you can try the recovery button below.');
            } else {
              toast.warning('Report generation is taking longer than expected. This may indicate an issue. Please wait or refresh if it continues.');
            }
            stuckWarningShown = true;

            // Check for extremely stuck sessions (2+ minutes)
            if (timeSinceLastUpdate > 120000) {
              console.error('❌ Session stuck for over 2 minutes - likely failed');
              toast.error('Report generation appears to have stalled. Please refresh the page and try again. If the issue persists, contact support.');
            }
          }
        }

        setCurrentStage(stage);
        setCurrentStep(step);
        setTotalSteps(total);
        setProgressPercent(percent);
        setCurrentPromptNumber(promptNum);
        setTotalPrompts(promptTotal);
        setCurrentPromptName(promptName);

        // Update estimated completion if available
        if (session.progress_details?.estimated_completion) {
          setEstimatedCompletion(session.progress_details.estimated_completion);
        }

        if (session.status === 'completed') {
          console.log(`✅ Session ${pollingSessionId} completed successfully`);
          console.log(`📊 Session completion signals:`, {
            status: session.status,
            report_id: session.report_id,
            completed_at: session.completed_at,
            current_step: session.current_step,
            current_prompt_number: session.current_prompt_number,
            total_prompts: session.total_prompts
          });

          // CRITICAL FIX: Don't wait for extraction - show report immediately
          // Extraction can complete in background, user wants to see their report now!
          const extractionStatus = session.extraction_status || 'pending';
          console.log(`🎨 Extraction status: ${extractionStatus} (will continue in background)`);

          // Check if we have a report_id - this is the key signal that report is ready
          if (!session.report_id) {
            console.warn('⚠️ Session marked complete but no report_id found yet, waiting...');
            setCurrentStage('Finalizing report...');
            setProgressPercent(99);
            // Keep polling - report should appear shortly
            return;
          }

          // CRITICAL FIX: Report is ready - show it immediately regardless of extraction status
          // Visualizations will load when user opens the report
          console.log('🎉 Report is ready to view! Preparing UI for completion...');

          // Show completion state - don't mention extraction to avoid confusion
          setCurrentStage('Report Complete!');
          setProgressPercent(100);
          setCurrentStep(total);

          // Show success message (extraction status is internal detail)
          if (extractionStatus === 'completed') {
            console.log('✅ Visualizations also ready');
          } else if (extractionStatus === 'failed') {
            console.warn('⚠️ Visualization extraction failed (user will see text version)');
          } else {
            console.log('🔄 Visualizations processing in background');
          }

          // Enhanced refresh with report_id verification
          const verifyAndRefreshReport = async () => {
            console.log('🔍 Verifying report creation and refreshing list...');

            // First, try to get the report_id from the session
            let reportIdFromSession: string | null = null;
            try {
              const { data: sessionData } = await supabase
                .from('processing_sessions')
                .select('report_id, title')
                .eq('id', pollingSessionId)
                .maybeSingle();

              if (sessionData?.report_id) {
                reportIdFromSession = sessionData.report_id;
                console.log('✅ Found report_id from session:', reportIdFromSession);
              }
            } catch (error) {
              console.warn('⚠️ Could not fetch report_id from session:', error);
            }

            // Retry logic with extended attempts and longer delays
            const refreshWithRetry = async (attempt = 1, maxAttempts = 5) => {
              console.log(`🔄 Attempt ${attempt}/${maxAttempts}: Refreshing reports list...`);

              // Wait for database replication (longer on first attempt)
              const delayMs = attempt === 1 ? 1500 : 2500;
              await new Promise(resolve => setTimeout(resolve, delayMs));

              // Strategy 1: If we have report_id, check for that specific report
              if (reportIdFromSession) {
                console.log(`🔍 Checking for specific report: ${reportIdFromSession}`);
                const { data: specificReport } = await supabase
                  .from('reports')
                  .select('id, title, created_at, status')
                  .eq('id', reportIdFromSession)
                  .eq('user_id', user?.id)
                  .maybeSingle();

                if (specificReport) {
                  console.log('✅ Report verified by ID:', specificReport);

                  // CRITICAL FIX: Clear generation state FIRST, then refresh and show toast
                  // This ensures Status Manager disappears immediately
                  console.log('🧹 Clearing generation state immediately...');
                  setPollingSessionId(null);
                  setIsGenerating(false);
                  setCanStartReport(true);
                  setActiveSessionInfo(null);
                  setCurrentStage('');
                  setCurrentStep(0);
                  setProgressPercent(0);
                  setCurrentPromptNumber(0);
                  setTotalPrompts(0);
                  setCurrentPromptName('');
                  setEstimatedCompletion(null);
                  sessionStorage.removeItem('activeReportSessionId');
                  console.log('✅ Generation state cleared, Status Manager should disappear now');

                  // Now refresh the reports list to show the new report
                  await fetchReports(false);

                  // Show success message
                  toast.success(`Report "${specificReport.title}" is ready to view!`);

                  return true;
                }
              }

              // Refresh the reports list before checking for recent reports
              await fetchReports(false);

              // Strategy 2: Check for any recent report (within 60 seconds)
              console.log('🔍 Checking for recent reports...');
              const { data: recentReports } = await supabase
                .from('reports')
                .select('id, title, created_at, status')
                .eq('user_id', user?.id)
                .order('created_at', { ascending: false })
                .limit(3);

              if (recentReports && recentReports.length > 0) {
                for (const report of recentReports) {
                  const reportAge = Date.now() - new Date(report.created_at).getTime();

                  // If report is very recent (within 60 seconds) and completed
                  if (reportAge < 60000 && report.status === 'completed') {
                    console.log('✅ Found recent completed report:', report);

                    // CRITICAL FIX: Clear generation state FIRST
                    console.log('🧹 Clearing generation state immediately...');
                    setPollingSessionId(null);
                    setIsGenerating(false);
                    setCanStartReport(true);
                    setActiveSessionInfo(null);
                    setCurrentStage('');
                    setCurrentStep(0);
                    setProgressPercent(0);
                    setCurrentPromptNumber(0);
                    setTotalPrompts(0);
                    setCurrentPromptName('');
                    setEstimatedCompletion(null);
                    sessionStorage.removeItem('activeReportSessionId');
                    console.log('✅ Generation state cleared, Status Manager should disappear now');

                    // Refresh the reports list one final time to ensure UI is synced
                    await fetchReports(false);

                    // Show success message
                    toast.success(`Report "${report.title}" is ready to view!`);

                    return true;
                  }
                }
              }

              // If we didn't find the report and have attempts left, try again
              if (attempt < maxAttempts) {
                console.log(`⚠️ Report not found yet, retrying in 2.5 seconds (attempt ${attempt + 1}/${maxAttempts})...`);
                return await refreshWithRetry(attempt + 1, maxAttempts);
              } else {
                console.warn('⚠️ Report generation completed but report not found in list after all retries');
                toast.warning('Report generated successfully! Please click the Refresh button to see it.', {
                  duration: 8000
                });

                // Clear generation state even if we couldn't find the report
                setPollingSessionId(null);
                setIsGenerating(false);
                setCanStartReport(true);
                setActiveSessionInfo(null);
                setCurrentStage('');
                setCurrentStep(0);
                setProgressPercent(0);
                setCurrentPromptNumber(0);
                setTotalPrompts(0);
                setCurrentPromptName('');
                setEstimatedCompletion(null);
                sessionStorage.removeItem('activeReportSessionId');

                return false;
              }
            };

            // Start the verification and refresh process
            await refreshWithRetry();
          };

          // Start verification immediately
          verifyAndRefreshReport();
        } else if (session.status === 'error') {
          console.error(`❌ Session ${pollingSessionId} failed:`, session.error_message);
          toast.error(`Report generation failed: ${session.error_message || 'Unknown error'}`);
          setPollingSessionId(null);
          setIsGenerating(false);
          setCanStartReport(true);
          setActiveSessionInfo(null);
          setCurrentStage('');
          setProgressPercent(0);
          setCurrentPromptNumber(0);
          setTotalPrompts(0);
          setCurrentPromptName('');
          setEstimatedCompletion(null);
          sessionStorage.removeItem('activeReportSessionId');
        } else if (session.status === 'processing') {
          // Update progress feedback
          const progressMessage = `Generating report... Step ${session.current_step}/${session.total_steps} (${session.current_stage || 'Processing'})`;
          console.log(`⏳ ${progressMessage}`);
        }
      } catch (error: any) {
        console.error('❌ handleSessionUpdate error:', error);

        if (error?.message?.includes('JWT') || error?.message?.includes('auth') || error?.code === 'PGRST301') {
          console.error('❌ Authentication error, stopping');
          toast.warning('Session expired. Please refresh the page to continue.');
          setPollingSessionId(null);
          setIsGenerating(false);
          setCanStartReport(true);
          setActiveSessionInfo(null);
          setCurrentStage('');
          setProgressPercent(0);
          setCurrentPromptNumber(0);
          setTotalPrompts(0);
          setCurrentPromptName('');
          setEstimatedCompletion(null);
          sessionStorage.removeItem('activeReportSessionId');
        } else {
          console.error('❌ Error processing session update:', error.message);
        }
      }
    };

    // Initial fetch to get current state
    const initializeSession = async () => {
      console.log(`🔄 Initial fetch for session: ${pollingSessionId}`);
      const { data: session, error } = await supabase
        .from('processing_sessions')
        .select('*')
        .eq('id', pollingSessionId)
        .maybeSingle();

      if (!error && session) {
        await handleSessionUpdate(session);
      } else {
        console.error('❌ Error fetching initial session state:', error);
      }
    };

    // Set up Realtime subscription with improved reliability
    const channel = supabase
      .channel(`processing-session-${pollingSessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'processing_sessions',
          filter: `id=eq.${pollingSessionId}`
        },
        (payload) => {
          console.log(`🔔 Realtime update received for session ${pollingSessionId}`);
          lastRealtimeUpdate = Date.now(); // Track when we last got a Realtime update
          handleSessionUpdate(payload.new);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Realtime subscription active for session ${pollingSessionId}`);
          realtimeActive = true;
          lastRealtimeUpdate = Date.now();

          // CRITICAL FIX: Hybrid polling that activates when Realtime goes silent
          // Check every 8 seconds if we should switch from Realtime to polling
          hybridPollInterval = setInterval(async () => {
            const timeSinceRealtimeUpdate = Date.now() - lastRealtimeUpdate;

            // If Realtime hasn't updated in 15+ seconds during processing, switch to polling
            if (timeSinceRealtimeUpdate > REALTIME_SILENT_TIMEOUT && realtimeActive) {
              console.warn(`⚠️ Realtime silent for ${Math.round(timeSinceRealtimeUpdate/1000)}s, activating polling mode`);
              realtimeActive = false;
            }

            // If in polling mode or as backup, fetch session state
            if (!realtimeActive || timeSinceRealtimeUpdate > 8000) {
              try {
                console.log(`🔄 ${realtimeActive ? 'Backup' : 'Active'} polling: Fetching session state`);
                const { data: session, error } = await supabase
                  .from('processing_sessions')
                  .select('*')
                  .eq('id', pollingSessionId)
                  .maybeSingle();

                if (!error && session) {
                  await handleSessionUpdate(session);
                  // If we got an update via polling, mark the time
                  lastRealtimeUpdate = Date.now();
                } else if (error) {
                  console.error('❌ Polling error:', error);
                }
              } catch (error) {
                console.warn('⚠️ Polling encountered error:', error);
              }
            }
          }, 8000); // Check every 8 seconds
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`⚠️ Realtime subscription failed, switching to active polling mode`);
          realtimeActive = false;

          // Fallback to more frequent polling if Realtime completely fails
          fallbackPollInterval = setInterval(async () => {
            try {
              const { data: session, error } = await supabase
                .from('processing_sessions')
                .select('*')
                .eq('id', pollingSessionId)
                .maybeSingle();

              if (!error && session) {
                await handleSessionUpdate(session);
              } else if (error) {
                console.error('❌ Polling error:', error);
              }
            } catch (error) {
              console.error('❌ Polling exception:', error);
            }
          }, 5000); // Poll every 5 seconds as fallback
        } else if (status === 'CLOSED') {
          console.log('🔌 Realtime subscription closed');
        } else {
          console.log(`🔔 Realtime subscription status: ${status}`);
        }
      });

    // Start with initial fetch
    initializeSession();

    // Set up stuck session detection (runs every 15 seconds)
    stuckCheckInterval = setInterval(() => {
      const timeSinceUpdate = Date.now() - lastProgressUpdate;
      if (timeSinceUpdate > STUCK_TIMEOUT && !stuckWarningShown) {
        console.warn('⚠️ Session appears stuck - no progress for 30+ seconds');
        toast.warning('Processing is taking longer than expected. This may be normal for complex reports.');
        stuckWarningShown = true;
      }
    }, 15000);

    // Cleanup function
    return () => {
      console.log(`🧹 Cleaning up Realtime subscription for session ${pollingSessionId}`);
      channel.unsubscribe();
      if (fallbackPollInterval) clearInterval(fallbackPollInterval);
      if (hybridPollInterval) clearInterval(hybridPollInterval);
      if (stuckCheckInterval) clearInterval(stuckCheckInterval);
    };
  }, [pollingSessionId, user?.id]);


  const fetchReports = async (showSpinner: boolean = true, retryCount: number = 0) => {
    if (!user) return;

    console.time('🔧 Reports - Fetch Reports');
    console.log('🚀 Reports: Starting reports fetch for user:', user.id);

    // Only set loading if we're not already refreshing
    if (showSpinner) {
      setIsLoading(true);
    }

    try {
      console.time('🔧 Reports - Database Query');
      console.log('📡 Reports: Querying reports table...');
      console.log('👤 Reports: Current user ID:', user.id);
      console.log('🔍 Reports: Query details - Table: reports, Filter: user_id =', user.id);

      // Small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch reports with extraction status from processing_sessions
      const { data, error } = await supabase
        .from('reports')
        .select(`
          *,
          processing_sessions!processing_sessions_report_id_fkey(
            extraction_status,
            extraction_error,
            extraction_completed_at
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      console.timeEnd('🔧 Reports - Database Query');
      console.log('📊 Reports: Query completed');
      console.log('📊 Reports: Error:', error ? JSON.stringify(error, null, 2) : 'None');
      console.log('📊 Reports: Data returned:', data ? `${data.length} reports` : 'null');

      // Extra diagnostic: Try to get ANY reports (bypassing user filter) to see if reports exist at all
      if ((!data || data.length === 0) && !error) {
        console.log('🔍 DIAGNOSTIC: No reports found for user. Checking if ANY reports exist in database...');
        const { data: anyReports, error: anyError } = await supabase
          .from('reports')
          .select('id, user_id, title, status')
          .limit(5);

        if (anyError) {
          console.log('❌ DIAGNOSTIC: Error querying any reports:', anyError);
        } else {
          console.log('🔍 DIAGNOSTIC: Found', anyReports?.length || 0, 'total reports in database');
          if (anyReports && anyReports.length > 0) {
            console.log('🔍 DIAGNOSTIC: Sample reports:', anyReports.map(r => ({
              id: r.id.substring(0, 8),
              user_id: r.user_id.substring(0, 8),
              status: r.status,
              title: r.title.substring(0, 30)
            })));
            console.log('⚠️ WARNING: Reports exist in database but none match current user ID!');
          }
        }
      }
      
      if (data && data.length > 0) {
        console.log('📋 Reports details:');
        data.forEach((report, index) => {
          console.log(`  ${index + 1}. ID: ${report.id}, Title: ${report.title}, Status: ${report.status}, User: ${report.user_id}, Created: ${report.created_at}`);
        });
      } else {
        console.log('📋 No reports found for user:', user.id);
        
        // Debug: Check if there are any reports for this user with different status
        const { data: allUserReports, error: debugError } = await supabase
          .from('reports')
          .select('*')
          .eq('user_id', user.id);
        
        if (!debugError && allUserReports) {
          console.log('🔍 Debug: All reports for user (any status):', allUserReports.length);
          allUserReports.forEach((report, index) => {
            console.log(`  Debug ${index + 1}. ID: ${report.id}, Title: ${report.title}, Status: ${report.status}, Created: ${report.created_at}`);
          });
        }
      }
      
      if (error) {
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          console.error('❌ Auth error fetching reports:', error);

          if (retryCount < 2) {
            console.log(`🔄 Retrying fetchReports (attempt ${retryCount + 1})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            return fetchReports(showSpinner, retryCount + 1);
          } else {
            toast.warning('Session may have expired. Please refresh the page if reports don\'t load.');
          }
        }
        throw error;
      }

      // Map the data to include extraction_status from processing_sessions
      const reportsWithExtraction = (data || []).map(report => ({
        ...report,
        extraction_status: report.processing_sessions?.[0]?.extraction_status || null
      }));

      setReports(reportsWithExtraction);
      console.log('✅ Reports: State updated with', reportsWithExtraction.length, 'reports');

      // Log extraction status for debugging
      reportsWithExtraction.forEach((report, index) => {
        if (report.status === 'completed') {
          console.log(`  Report ${index + 1}: ${report.title} - Extraction: ${report.extraction_status || 'unknown'}`);
        }
      });
    } catch (error: any) {
      console.error('Error fetching reports:', error);

      if (!error.message?.includes('JWT') && !error.message?.includes('auth')) {
        toast.error('Failed to load reports');
      }
    } finally {
      console.timeEnd('🔧 Reports - Fetch Reports');
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleCreateReport = async () => {
    console.log('🎯 handleCreateReport called');
    console.log('👤 Current user:', user?.id, user?.email);

    if (!user) {
      console.error('❌ No user found');
      toast.error('You must be logged in to generate reports');
      return;
    }

    if (!accessControl.isAdmin && !accessControl.hasSetupFeePaid) {
      console.log('🔒 Setup fee not paid, showing paywall');
      setShowPaywall(true);
      return;
    }

    console.log('✅ User validated, checking if report can be started...');

    // CRITICAL: Check if user can start a new report BEFORE setting UI state
    try {
      setIsCheckingSession(true);
      console.log('🔍 Validating session state before starting report...');

      const canStartResult = await canStartNewReport(user.id);

      if (!canStartResult.can_start) {
        console.warn('⚠️ Cannot start new report:', canStartResult.message);

        // Check if this is a graceful degradation message (validation unavailable)
        if (canStartResult.message.includes('validation unavailable') ||
            canStartResult.message.includes('Unable to validate')) {
          // Show warning but allow user to proceed
          toast.warning(canStartResult.message, { duration: 6000 });
          console.log('⚠️ Proceeding with report generation despite validation warning');
        } else {
          // Hard block - there's an active session
          toast.warning(canStartResult.message);

          // If there's an active session, try to resume it
          if (canStartResult.active_session) {
            console.log('📋 Resuming existing active session...');
            setActiveSessionInfo(canStartResult.active_session as any);
            setPollingSessionId(canStartResult.active_session.session_id);
            setIsGenerating(true);
            setCurrentStage(canStartResult.active_session.current_stage);
            setProgressPercent(canStartResult.active_session.progress_percent);
            sessionStorage.setItem('activeReportSessionId', canStartResult.active_session.session_id);
            toast.info('Resuming your existing report generation...');
          }

          setIsCheckingSession(false);
          return;
        }
      } else {
        console.log('✅ User can start new report:', canStartResult.message);

        // Check if there was a cleaned session
        if (canStartResult.cleaned_session) {
          console.log(`🧹 Previous stuck session was cleaned up: ${canStartResult.cleaned_session}`);
          toast.info('Previous incomplete session was cleaned up. Starting fresh...');
        }
      }

      setIsCheckingSession(false);
    } catch (error: any) {
      console.error('❌ Error checking if user can start report:', error);
      setIsCheckingSession(false);

      // Show helpful error message based on error type
      if (error.message?.includes('Authentication session expired')) {
        toast.error('Your session has expired. Please refresh the page and try again.');
        return;
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        toast.warning('Network issue detected. You can try again or refresh the page.', { duration: 5000 });
        // Allow user to retry rather than blocking completely
      } else {
        toast.warning('Unable to verify session state. You may proceed, but please ensure no other reports are currently generating.', { duration: 6000 });
        // Don't return - allow user to proceed with caution
      }
    }

    console.log('✅ Session check passed, starting report generation process');

    // Set status UI flags IMMEDIATELY so status window appears right away
    setIsGenerating(true);
    setCanStartReport(false);
    setCurrentStage('Preparing report generation...');
    setProgressPercent(0);
    setCurrentStep(0);
    setTotalSteps(9);
    toast.info('Preparing report generation...');

    try {
      // Update status: Validating user data
      setCurrentStage('Validating your documents...');
      setProgressPercent(5);
      console.log('🔍 Pre-generation validation: Checking user data...');

      // Check documents
      console.log('📄 Fetching documents for user:', user.id);
      const { data: documents, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id);

      if (docsError) {
        console.error('❌ Document fetch error:', docsError);
        throw new Error(`Failed to verify documents: ${docsError.message}`);
      }

      console.log('📄 Documents found:', documents?.length, documents);

      // Validate resume exists and has valid extracted content
      const resumeDoc = documents?.find(doc => doc.type === 'resume');

      if (!resumeDoc) {
        toast.error('You must upload a resume before generating a report. Please go to My Profile to upload your resume.');
        setIsGenerating(false);
        return;
      }

      // Check if extraction is still pending
      if (resumeDoc.text_extraction_status === 'pending' || resumeDoc.status === 'processing') {
        toast.error('Your resume is still being processed. Please wait a moment for text extraction to complete, then try again.');
        setIsGenerating(false);
        return;
      }

      // Check if extraction failed
      if (resumeDoc.text_extraction_status === 'failed' || resumeDoc.status === 'error') {
        const errorMsg = resumeDoc.extraction_error_message || 'Text extraction failed';
        toast.error(`Resume extraction failed: ${errorMsg}. Please re-upload your resume as a text-based PDF for best results.`);
        setIsGenerating(false);
        return;
      }

      // Validate extracted content exists and has sufficient length
      if (!resumeDoc.extracted_content) {
        toast.error('Your resume does not have extracted content. Please re-upload your resume ensuring it contains extractable text.');
        setIsGenerating(false);
        return;
      }

      if (resumeDoc.extracted_content.length < 100) {
        toast.error(`Your resume text is too short (${resumeDoc.extracted_content.length} characters). Please ensure your PDF contains proper text content and re-upload.`);
        setIsGenerating(false);
        return;
      }

      // All validations passed
      console.log(`✅ Resume validation passed: ${resumeDoc.extracted_content.length} characters, status: ${resumeDoc.text_extraction_status}, method: ${resumeDoc.extraction_method}`);

      console.log('✅ Pre-generation validation completed successfully');

      // Get user's name from profile or fallback to email
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();

      const userName = profile?.full_name || user.email?.split('@')[0] || 'User';
      const now = new Date();
      const reportTitle = `${userName} Career Intelligence Report - ${now.toLocaleDateString()} ${now.toLocaleTimeString()}.${now.getMilliseconds()}`;

      // Update status: Data validated
      setCurrentStage('Data validated. Initializing...');
      setProgressPercent(10);

      try {
      console.log('🚀 Initiating comprehensive report generation...');
      console.log('👤 Current user ID:', user.id);
      console.log('📋 Report title:', reportTitle);

      // Update status: Previous sessions already checked
      setCurrentStage('Starting report generation...');

      // Update status: Starting report generation
      console.log('✅ Validation complete, starting Edge Function call');
      setCurrentStage('Starting report generation...');
      setProgressPercent(15);

      // Try batch orchestrator first (safer for timeout issues)
      console.log('🎯 Attempting batch processing mode...');
      let result;
      let error;
      let usedFallback = false;

      try {
        const batchResult = await supabase.functions.invoke('generate-report-orchestrator', {
          body: {
            userId: user.id,
            reportTitle: reportTitle
          }
        });

        result = batchResult.data;
        error = batchResult.error;

        // Check if batch mode failed to start
        if (error || !result?.success) {
          console.warn('⚠️ Batch mode failed to start, will try direct mode:', error || result);
          throw new Error(error?.message || result?.error || 'Batch mode unavailable');
        }

        console.log('✅ Batch report generation initiated:', result);

      } catch (batchError: any) {
        console.warn('⚠️ Batch processing failed to start:', batchError.message);
        console.log('🔄 Falling back to direct report generation...');

        // Only show fallback message to user, not "unavailable"
        usedFallback = true;

        // Fallback to direct generate-report function
        try {
          const directResult = await supabase.functions.invoke('generate-report', {
            body: {
              userId: user.id,
              reportTitle: reportTitle
            }
          });

          result = directResult.data;
          error = directResult.error;

          if (error) {
            console.error('❌ Direct mode error:', error);
            throw new Error(`Report generation failed: ${error.message}`);
          }

          if (!result?.success) {
            console.error('❌ Direct mode returned error:', result);
            throw new Error(result?.error || 'Report generation failed');
          }

          console.log('✅ Direct report generation initiated:', result);

        } catch (directError: any) {
          console.error('❌ Both batch and direct modes failed:', directError);
          throw new Error(`Report generation failed: ${directError.message}`);
        }
      }

      if (error) {
        console.error('❌ Edge function error:', error);
        throw new Error(`Report generation failed: ${error.message}`);
      }

      if (!result?.success) {
        console.error('❌ Edge function returned error:', result);
        throw new Error(result?.error || 'Report generation failed');
      }

      console.log('📊 Session details:', {
        sessionId: result.sessionId,
        mode: result.mode || (usedFallback ? 'direct' : 'batch'),
        message: result.message,
        usedFallback
      });

      // CRITICAL FIX: Set polling session ID IMMEDIATELY to activate Realtime subscription
      if (result.sessionId) {
        const mode = result.mode || (usedFallback ? 'direct' : 'batch');
        console.log(`🔄 Starting ${mode} processing tracking for session: ${result.sessionId}`);

        // Set these FIRST to trigger the status window and Realtime subscription immediately
        setPollingSessionId(result.sessionId);
        sessionStorage.setItem('activeReportSessionId', result.sessionId);

        // Set initial UI state to show activity immediately
        setCurrentStage('Initializing report generation...');
        setProgressPercent(5);
        setCurrentStep(0);
        setTotalSteps(9);

        console.log(`✅ Status window activated with session: ${result.sessionId}`);
        console.log(`📊 Realtime subscription will now track all progress updates`);

        const modeMessage = usedFallback
          ? 'Report generation started successfully. Processing all prompts in a single execution.'
          : 'Report generation started successfully. Processing prompts in batches for optimal reliability.';
        toast.success(modeMessage);

        // The Realtime subscription effect will now activate and track all updates
      } else {
        console.error('❌ No session ID returned from function');
        toast.error('Failed to start report generation. Please try again.');
        setIsGenerating(false);
        setCanStartReport(true);
        return;
      }
      
      // Force refresh reports list immediately since generation completed
      setTimeout(() => {
        console.log('🔄 Force refreshing reports list after immediate completion...');
        fetchReports(false);
      }, 1000);

      } catch (error: any) {
        console.error('❌ Comprehensive report initiation error:', error);

        // Provide specific error messages for common issues
        let errorMessage = error.message || 'Failed to initiate report generation';

        if (errorMessage.includes('Invalid document data')) {
          toast.error(`Data validation failed: ${errorMessage}`);
        } else if (errorMessage.includes('Resume document is missing')) {
          toast.error('Cannot generate report: Resume is required. Please upload your resume first.');
        } else if (errorMessage.includes('invalid extracted content')) {
          toast.error('Cannot generate report: Your documents have invalid or placeholder data. Please re-upload with extractable PDFs.');
        } else {
          toast.error(errorMessage);
        }

        // Only reset isGenerating on error - keep it true if session started successfully
        setIsGenerating(false);
      }

    } catch (error: any) {
      console.error('❌ Pre-generation validation error:', error);
      toast.error(error.message || 'Failed to validate data for report generation');
      setIsGenerating(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    toast.info('Refreshing reports...');
    await fetchReports(true);
  };

  const handleRecoverSession = async () => {
    if (!pollingSessionId) {
      toast.error('No active session to recover');
      return;
    }

    setIsRecovering(true);
    toast.info('Attempting to recover stuck session...');

    try {
      const { data, error } = await supabase.functions.invoke('recover-stuck-session', {
        body: { sessionId: pollingSessionId }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        if (data.action === 'recovered') {
          toast.success(`Session recovery initiated. Processing will resume from batch ${data.batchToResume}.`);
        } else if (data.action === 'no_action') {
          toast.info(data.message);
        } else if (data.action === 'marked_as_error') {
          toast.warning('Session was marked as error. Please start a new report generation.');
          setPollingSessionId(null);
          setIsGenerating(false);
          setCanStartReport(true);
          setActiveSessionInfo(null);
          sessionStorage.removeItem('activeReportSessionId');
        }
      }
    } catch (error: any) {
      console.error('Recovery error:', error);
      toast.error(`Failed to recover session: ${error.message}`);
    } finally {
      setIsRecovering(false);
    }
  };

  const handleCancelSession = async () => {
    if (!pollingSessionId || !user) {
      console.error('❌ Cancel attempted but no session or user');
      toast.error('No active session to cancel');
      return;
    }

    console.log(`🔴 Cancel requested for session: ${pollingSessionId.substring(0, 8)}...`);

    if (!confirm('Are you sure you want to cancel this report generation?')) {
      console.log('🚫 User cancelled the cancel operation');
      return;
    }

    setIsCancelling(true);
    toast.info('Cancelling report generation...');

    // Helper function to clear all UI state
    const clearAllState = () => {
      console.log('🧹 Clearing all UI state...');
      setPollingSessionId(null);
      setIsGenerating(false);
      setCanStartReport(true);
      setActiveSessionInfo(null);
      setCurrentStage('');
      setProgressPercent(0);
      setCurrentStep(0);
      setCurrentPromptNumber(0);
      setTotalPrompts(0);
      setCurrentPromptName('');
      setEstimatedCompletion(null);
      sessionStorage.removeItem('activeReportSessionId');
      console.log('✅ All UI state cleared');
    };

    try {
      console.log(`🔄 Calling cancelSession for ${pollingSessionId.substring(0, 8)}...`);

      // Always use force mode for guaranteed success
      await cancelSession(pollingSessionId, user.id, true);

      console.log('✅ Cancel successful via service layer');

      // Clear all state immediately after successful cancel
      clearAllState();

      toast.success('Report generation cancelled successfully');

      // Refresh reports list to show updated status
      console.log('🔄 Refreshing reports list...');
      await fetchReports(false);
      console.log('✅ Reports list refreshed');
    } catch (error: any) {
      console.error('❌ Error cancelling session:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });

      // CRITICAL FAILSAFE: Always clear UI state even if cancel fails
      // This prevents the UI from being stuck in a bad state
      console.warn('⚠️ Cancel failed, but clearing UI state anyway as failsafe');
      clearAllState();

      // Show appropriate error message
      if (error.message?.includes('session has expired')) {
        toast.error('Your session has expired. The UI has been reset.', { duration: 6000 });
        toast.info('Please refresh the page to continue.', { duration: 6000 });
      } else if (error.message?.includes('no longer exists')) {
        toast.warning('Session no longer exists in database. UI has been reset.', { duration: 5000 });
      } else {
        toast.error(`Cancel operation encountered an issue: ${error.message}`, { duration: 6000 });
        toast.info('UI state has been reset. The session should stop processing shortly.', { duration: 6000 });
      }

      // Attempt to refresh reports list even after error
      try {
        await fetchReports(false);
      } catch (refreshError) {
        console.error('❌ Failed to refresh reports after cancel error:', refreshError);
      }
    } finally {
      setIsCancelling(false);
      console.log('✅ handleCancelSession completed');
    }
  };

  const handleRetryExtraction = async (reportId: string) => {
    console.log('🎨 Retry extraction function called with reportId:', reportId);

    if (!confirm('Retry visualization extraction for this report?')) return;

    console.log('✅ User confirmed retry, proceeding...');

    try {
      console.log('🔄 Attempting to trigger visualization extraction...');

      const { data, error } = await supabase.functions.invoke('extract-career-intelligence-data', {
        body: {
          userId: user?.id,
          reportId: reportId,
          forceRetry: true
        }
      });

      if (error) throw error;

      console.log('✅ Extraction retry triggered successfully:', data);
      toast.success('Visualization extraction started. Please wait a moment and refresh.');

      // Refresh reports after a delay to show updated status
      setTimeout(() => {
        fetchReports(false);
      }, 3000);
    } catch (error: any) {
      console.error('❌ Error retrying extraction:', error);
      toast.error(`Failed to retry extraction: ${error.message}`);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    console.log('🗑️ Delete report function called with reportId:', reportId);
    console.log('🔍 Current user:', user?.id);
    console.log('📊 Current reports state:', reports.length, 'reports');

    if (!confirm('Are you sure you want to delete this report?')) return;

    console.log('✅ User confirmed deletion, proceeding...');

    try {
      console.log('🔄 Attempting to delete report from database...');
      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      console.log('📡 Supabase delete response - error:', error);

      if (error) throw error;

      console.log('✅ Report deleted successfully from database');
      toast.success('Report deleted successfully');
      console.log('🔄 Refreshing reports list...');
      fetchReports(false);
    } catch (error) {
      console.error('❌ Error deleting report:', error);
      console.error('❌ Full error object:', JSON.stringify(error, null, 2));
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error code:', error?.code);
      console.error('❌ Error details:', error?.details);
      toast.error('Failed to delete report');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'queued':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">FutureLens Reports</h1>
            <p className="text-gray-600 mt-2">
              Generate comprehensive AI-powered career analysis reports based on your profile and documents.
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing || isGenerating || isCheckingSession}
              variant="outline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleCreateReport}
              disabled={isGenerating || !canStartReport || isCheckingSession}
              title={!canStartReport ? 'A report is currently being generated' : 'Generate a new career intelligence report'}
            >
              {isCheckingSession ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-r-transparent mr-2" />
                  Checking...
                </>
              ) : isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-r-transparent mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate New Report
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Report Generation Progress */}
        {pollingSessionId && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center flex-1">
                <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-r-transparent mr-4"></div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900">Generating Your Career Intelligence Report</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    {getDetailedStageDescription()}
                  </p>
                  {currentPromptName && totalPrompts > 0 && (
                    <div className="mt-1">
                      <p className="text-xs text-blue-600 font-medium">
                        🤖 {currentPromptName}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-blue-100 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${(currentPromptNumber / totalPrompts) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-blue-500 font-mono">
                          {currentPromptNumber}/{totalPrompts}
                        </span>
                      </div>
                    </div>
                  )}
                  {estimatedCompletion && (
                    <p className="text-xs text-blue-500 mt-1">
                      Estimated completion: {new Date(estimatedCompletion).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-900">{progressPercent}%</div>
                <div className="text-xs text-blue-600">Step {currentStep}/{totalSteps}</div>
                {totalPrompts > 0 && currentPromptNumber > 0 && (
                  <div className="text-xs text-blue-500 mt-1">
                    {currentPromptNumber} of {totalPrompts} prompts
                  </div>
                )}
              </div>
            </div>

            <div className="w-full bg-blue-200 rounded-full h-3 mb-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              >
                <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
              </div>
            </div>

            <div className="bg-white/50 backdrop-blur-sm rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Processing Stages:
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {getProcessingStages().map((stage, idx) => {
                  // Map stage index (0-8) to currentStep (0-8)
                  // Stage at index idx is:
                  //   - completed when currentStep > idx
                  //   - in progress when currentStep === idx
                  //   - pending when currentStep < idx
                  const isCompleted = currentStep > idx;
                  const isInProgress = currentStep === idx;

                  return (
                    <div
                      key={idx}
                      className={`flex items-center ${
                        isCompleted ? 'text-green-700' :
                        isInProgress ? 'text-blue-700 font-medium' :
                        'text-gray-500'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      ) : isInProgress ? (
                        <RefreshCw className="h-4 w-4 mr-2 text-blue-600 animate-spin" />
                      ) : (
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      )}
                      {stage}
                    </div>
                  );
                })}
              </div>
            </div>

            {pollingSessionId && (
              <div className="mt-4 border-t border-blue-200 pt-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-blue-500">
                    <p className="font-medium">Session ID: {pollingSessionId}</p>
                    {activeSessionInfo && (
                      <p className="mt-1">
                        Started: {new Date(activeSessionInfo.started_at).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleRecoverSession}
                      disabled={isRecovering || isCancelling}
                      className="px-3 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Try to recover if session appears stuck"
                    >
                      {isRecovering ? 'Recovering...' : 'Recover'}
                    </button>
                    <button
                      onClick={handleCancelSession}
                      disabled={isCancelling || isRecovering}
                      className="px-3 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      title="Cancel this report generation"
                    >
                      {isCancelling ? (
                        'Cancelling...'
                      ) : (
                        <>
                          <Ban className="h-3 w-3 mr-1" />
                          Cancel
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No reports yet</h3>
                <p className="text-muted-foreground mb-6">
                  Generate your first AI-powered career analysis report to get started.
                </p>
                <Button onClick={handleCreateReport} disabled={isGenerating}>
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-r-transparent mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Generate Your First Report
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reports.map((report) => (
                      <tr key={report.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {report.title}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(report.status)}
                            <span className={`ml-2 text-sm font-medium ${
                              report.status === 'completed' ? 'text-green-600' :
                              report.status === 'processing' ? 'text-blue-600' :
                              report.status === 'error' ? 'text-red-600' :
                              report.status === 'queued' ? 'text-yellow-600' : 'text-gray-600'
                            }`}>
                              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {report.status === 'completed' && (
                              <Link to={`/reports/${report.id}`}>
                                <Button variant="ghost" size="sm" title="View Report">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </Link>
                            )}
                            {report.status === 'completed' && report.extraction_status === 'failed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRetryExtraction(report.id)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Retry Visualization Extraction"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReport(report.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Report"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureName="Career Intelligence Reports"
      />
    </div>
  );
};

export default Reports